import { CSSProperties, useState } from "react";
import { Check, ChevronDown, ArrowRight } from "lucide-react";
import { openSelarStore } from "../shared";
import {
  okrExecutionModuleBg,
  strategicOwnershipModuleBg,
  performanceAppraisalKitModuleBg,
} from "assets/website";

interface ModuleData {
  title: string;
  /** Short label for the mobile "Get …" CTA */
  ctaLabel: string;
  description: string;
  whatYouGet: string[];
  whatItDoes: string[];
  panelBg: string;
  /** Panel colour while the card is hovered, alongside the CTA reveal */
  panelHoverBg: string;
  borderColor: string;
  bgImage: string;
  /** Watermark rotation in degrees */
  bgRotation: number;
  /** Watermark CSS width (e.g. "130%" or "900px") */
  bgWidth: string;
  /** Watermark offset from the card's top edge */
  bgTop: string;
  /** Watermark offset from the card's left edge */
  bgLeft: string;
}

const modules: ModuleData[] = [
  {
    title: "OKR Execution System",
    ctaLabel: "Get OKR Execution System",
    description:
      "A system that gives your team a structured way to plan, execute, and review work without confusion or overload.",
    whatYouGet: [
      "OKR planning sheet (with V/O/M classification)",
      "Weekly execution tracker",
      "Progress calculation logic",
      "Executive dashboard",
      "Leadership review prompts",
    ],
    whatItDoes: [
      "Turn company goals into structured execution plans",
      "Track progress weekly",
      "Identify performance risks early",
      "Separate growth work from operational work",
      "Give leadership clear visibility into execution health",
    ],
    panelBg: "#F2F6FF",
    panelHoverBg: "#DCE9FF",
    borderColor: "#C6E0FF",
    bgImage: okrExecutionModuleBg,
    bgRotation: 39,
    bgWidth: "100%",
    bgTop: "33%",
    bgLeft: "-27%",
  },
  {
    title: "Strategic Ownership Operating Framework™",
    ctaLabel: "Get SOOF™",
    description:
      "A layer between assigned goals and actual execution that helps install ownership into your organisation as a system.",
    whatYouGet: [
      "Role ownership statement",
      "OKR translation layer",
      "30/60/90 Execution plan",
      "Weekly operating rhythm template",
      "Weekly progress report format",
    ],
    whatItDoes: [
      "Translate OKRs into personal execution plans",
      "Structure weekly work rhythms",
      "Report progress consistently",
      "Identify execution gaps early",
      "Clearly define what you own",
    ],
    panelBg: "#FFFAED",
    panelHoverBg: "#FFF6DB",
    borderColor: "#FFE9A7",
    bgImage: strategicOwnershipModuleBg,
    bgRotation: 49,
    bgWidth: "90%",
    bgTop: "13%",
    bgLeft: "-30%",
  },
  {
    title: "Performance Appraisal Kit™",
    ctaLabel: "Get Performance Appraisal Kit™",
    description:
      "A structured, evidence-based review system that helps evaluate performance using the same framework before you ever sit down for the conversation.",
    whatYouGet: [
      "Mid-cycle check-in",
      "Employee self-assessment",
      "Manager assessment",
      "Performance gap analysis",
      "Review summary & development plan",
    ],
    whatItDoes: [
      "Run structured performance reviews",
      "Compare employee and manager assessments",
      "Identify perception gaps early",
      "Standardise evaluation across teams",
      "Document development commitments",
    ],
    panelBg: "#F1F1F1",
    panelHoverBg: "#DDDDDD",
    borderColor: "#CECECE",
    bgImage: performanceAppraisalKitModuleBg,
    bgRotation: 38,
    bgWidth: "100%",
    bgTop: "32%",
    bgLeft: "-25%",
  },
];

/** Checklist that collapses into an accordion on mobile and stays open on lg+. */
const CheckList: React.FC<{
  heading: string;
  items: string[];
  borderColor: string;
}> = ({ heading, items, borderColor }) => {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-4 text-left border-b pb-4 px-4 lg:border-b-0 lg:pb-0 lg:cursor-default"
        style={{ borderColor }}
      >
        <span className="text-base font-normal text-[#344054]">{heading}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-[#344054] transition-transform duration-200 lg:hidden ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <ul className={`space-y-2 mt-5 ${open ? "block px-4" : "hidden"} lg:block`}>
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5">
            <Check
              className="mt-1 h-4 w-4 shrink-0 text-[#2D9829]"
              strokeWidth={3}
            />
            <span className="text-[15px] md:text-base text-[#1D2939] leading-snug">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ExploreModules = () => {
  return (
    <section className="py-16 md:py-24 lg:px-6 px-4 bg-[#F9FAFB]">
      <div className="container mx-auto sm:px-4 !px-0">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2
            data-aos="fade-up"
            className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17] mb-4"
          >
            Explore The Modules
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-base md:text-lg lg:text-xl text-[#475467]"
          >
            Every formula spreadsheet, and appraisal check-in is linked to form
            a single, synchronized workflow loop.
          </p>
        </div>

        {/* Module rows */}
        <div className="space-y-6 md:space-y-8">
          {modules.map((module, index) => (
            <div
              key={module.title}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="group relative overflow-hidden rounded-[20px] border grid lg:grid-cols-2 bg-[var(--panel-bg)] transition-colors duration-200 lg:hover:bg-[var(--panel-bg-hover)]"
              style={
                {
                  // Held as custom properties rather than a `backgroundColor`
                  // style: an inline background would outrank the hover class.
                  "--panel-bg": module.panelBg,
                  "--panel-bg-hover": module.panelHoverBg,
                  borderColor: module.borderColor,
                } as CSSProperties
              }
            >
              {/* Faint rotated document watermark */}
              <img
                src={module.bgImage}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute max-w-none select-none"
                style={{
                  width: module.bgWidth,
                  top: module.bgTop,
                  left: module.bgLeft,
                  opacity: 0.03,
                  transform: `rotate(${module.bgRotation}deg)`,
                }}
              />

              {/* Left: title + description (+ the desktop CTA, pinned bottom) */}
              <div
                className="relative flex flex-col p-6 md:p-10 lg:p-12 lg:border-r"
                style={{ borderColor: module.borderColor }}
              >
                <h3 className="text-xl md:text-2xl lg:text-[32px] font-display font-semibold text-[#050B17] mb-4">
                  {module.title}
                </h3>
                <p className="text-base md:text-lg text-[#1D2939] leading-relaxed">
                  {module.description}
                </p>

                {/* Desktop CTA — revealed on hover. `mt-auto` sits it against the
                    bottom of the column, and the space is reserved at all times so
                    revealing it never reflows the card. Stays inert while hidden,
                    but still reachable by keyboard. */}
                <div className="mt-auto hidden lg:flex pt-10">
                  <button
                    onClick={openSelarStore}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[#012169] hover:bg-[#011550] text-white text-base font-medium rounded-[10px] opacity-0 translate-y-1 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:translate-y-0 focus-visible:pointer-events-auto"
                  >
                    {module.ctaLabel}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Right: what you get (top) / what it does (bottom) */}
              <div className="relative">
                <div
                  className="p-6 md:p-10 lg:p-12 pl-3 md:pl-4 lg:pl-5 pb-3 md:pb-4 lg:pb-6 lg:border-b"
                  style={{ borderColor: module.borderColor }}
                >
                  <CheckList
                    heading="What You Get:"
                    items={module.whatYouGet}
                    borderColor={module.borderColor}
                  />
                </div>
                <div className="p-6 md:p-10 lg:p-12  pl-3 md:pl-4 lg:pl-5 pb-3 md:pb-4 lg:pb-6">
                  <CheckList
                    heading="What It Does:"
                    items={module.whatItDoes}
                    borderColor={module.borderColor}
                  />
                </div>

                {/* Mobile CTA */}
                <div className="relative z-10 flex flex-col gap-3 px-6 pb-8 pt-6 lg:hidden">
                  <button
                    onClick={openSelarStore}
                    className="flex w-full items-center justify-center gap-2 px-6 py-3 bg-[#012169] hover:bg-[#011550] text-white text-base font-medium rounded-[10px] transition-all duration-200"
                  >
                    {module.ctaLabel}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { ExploreModules };
